/**
 * A small router. Telegram notifications deep-link to /onay/:id and /istek/:id, so paths
 * are real URLs (the preview and production servers fall back to index.html). The static
 * preview, served from a sub-path without a fallback, uses the same paths after "#".
 */

import { type AnchorHTMLAttributes, type MouseEvent, type ReactNode, useSyncExternalStore } from 'react';

export type SectionId =
  | 'ozet'
  | 'leadler'
  | 'potansiyel-musteriler'
  | 'tanitim-gonderimi'
  | 'tanitim-takibi'
  | 'satislar'
  | 'satis-haritasi'
  | 'ilanlar'
  | 'urunler'
  | 'musteriler'
  | 'site'
  | 'site-sohbetleri'
  | 'kanallar'
  | 'seslendirme'
  | 'seo'
  | 'yapilanlar'
  | 'bilgi-bankasi'
  | 'sekreter'
  | 'gecmis-cevaplar'
  | 'gonderilecekler'
  | 'kayitlar';

/** Leadler sub-tabs: /leadler/harita, /leadler/huni, /leadler/ogrenme. */
export type LeadsTab = 'harita' | 'huni' | 'ogrenme';

export const LEADS_TABS: readonly LeadsTab[] = ['harita', 'huni', 'ogrenme'];

/** Ayarlar sub-pages: desktop tabs, and phone sub-screens pushed from the Ayarlar list. */
export type SettingsTab =
  | 'kurulum'
  | 'baglantilar'
  | 'api-kullanimi'
  | 'politika'
  | 'butceler'
  | 'onay-sureleri'
  | 'sert-sinirlar'
  | 'ajanlar'
  | 'takvim'
  | 'tercihler'
  | 'bildirimler'
  | 'dil'
  | 'veri'
  | 'guvenlik'
  | 'hakkinda'
  | 'degisiklikler';

export const SETTINGS_TABS: readonly SettingsTab[] = [
  'kurulum',
  'baglantilar',
  'api-kullanimi',
  'politika',
  'butceler',
  'onay-sureleri',
  'sert-sinirlar',
  'ajanlar',
  'takvim',
  'tercihler',
  'bildirimler',
  'dil',
  'veri',
  'guvenlik',
  'hakkinda',
  'degisiklikler',
];

export type Route =
  | { name: 'bugun' }
  | { name: 'onaylar' }
  | { name: 'onay'; id: string }
  | { name: 'istekler'; focus: string | null }
  | { name: 'ekip'; run: number | null }
  /** One member of the team and what they did (/ekip/kisi/temsilci, /ekip/kisi/sercan). */
  | { name: 'kisi'; id: string }
  | { name: 'diger' }
  | { name: 'section'; section: SectionId }
  /** batch: the learning approval batch (/leadler/ogrenme/paket), a detail screen. */
  | { name: 'leadler'; tab: LeadsTab; batch: boolean }
  | { name: 'ayarlar'; tab: SettingsTab | null }
  | { name: 'baglanti'; id: string }
  | { name: 'notfound' };

export const SECTIONS: readonly SectionId[] = [
  'ozet',
  'leadler',
  'potansiyel-musteriler',
  'tanitim-gonderimi',
  'tanitim-takibi',
  'satislar',
  'satis-haritasi',
  'ilanlar',
  'urunler',
  'musteriler',
  'site',
  'site-sohbetleri',
  'kanallar',
  'seslendirme',
  'seo',
  'yapilanlar',
  'bilgi-bankasi',
  'sekreter',
  'gecmis-cevaplar',
  'gonderilecekler',
  'kayitlar',
];

export function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean).map(decodeURIComponent);
  const [first, second] = parts;
  if (parts.length === 0) return { name: 'bugun' };
  if (first === 'onaylar' && parts.length === 1) return { name: 'onaylar' };
  if (first === 'onay' && second && parts.length === 2) return { name: 'onay', id: second };
  if (first === 'istekler' && parts.length === 1) return { name: 'istekler', focus: null };
  if (first === 'istek' && second && parts.length === 2) return { name: 'istekler', focus: second };
  if (first === 'ekip' && parts.length === 1) return { name: 'ekip', run: null };
  if (first === 'ekip' && second && parts.length === 2 && /^\d+$/.test(second))
    return { name: 'ekip', run: Number(second) };
  if (first === 'ekip' && second === 'kisi' && parts.length === 3 && /^[a-z][a-z0-9_-]{1,39}$/.test(parts[2] ?? ''))
    return { name: 'kisi', id: parts[2] as string };
  if (first === 'diger' && parts.length === 1) return { name: 'diger' };
  if (first === 'ayarlar') {
    if (parts.length === 1) return { name: 'ayarlar', tab: null };
    const third = parts[2];
    if (second === 'baglanti' && third && parts.length === 3) return { name: 'baglanti', id: third };
    if (second && parts.length === 2 && (SETTINGS_TABS as readonly string[]).includes(second)) {
      return { name: 'ayarlar', tab: second as SettingsTab };
    }
    return { name: 'notfound' };
  }
  if (first === 'leadler') {
    if (parts.length === 1) return { name: 'leadler', tab: 'harita', batch: false };
    if (parts.length === 2 && (LEADS_TABS as readonly string[]).includes(second ?? '')) {
      return { name: 'leadler', tab: second as LeadsTab, batch: false };
    }
    if (parts.length === 3 && second === 'ogrenme' && parts[2] === 'paket') {
      return { name: 'leadler', tab: 'ogrenme', batch: true };
    }
    return { name: 'notfound' };
  }
  // Audit 25 Oct 2026 (F7): the old /politika section was an empty desktop page; the rules live in Ayarlar.
  if (first === 'politika' && parts.length === 1) return { name: 'ayarlar', tab: 'politika' };
  if (first && (SECTIONS as readonly string[]).includes(first) && parts.length === 1) {
    return { name: 'section', section: first as SectionId };
  }
  return { name: 'notfound' };
}

const NAVIGATE_EVENT = 'twin:navigate';

/**
 * history: real paths (/onay/214), served by Caddy and vite with an index.html fallback.
 * hash: the path lives after "#" (…/index.html#/onay/214), for the static preview served
 * from a sub-path of another site where no fallback exists.
 */
export type RouterMode = 'history' | 'hash';

let mode: RouterMode = 'history';

export function setRouterMode(next: RouterMode): void {
  mode = next;
}

export function routerMode(): RouterMode {
  return mode;
}

function subscribe(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  window.addEventListener('hashchange', listener);
  window.addEventListener(NAVIGATE_EVENT, listener);
  return () => {
    window.removeEventListener('popstate', listener);
    window.removeEventListener('hashchange', listener);
    window.removeEventListener(NAVIGATE_EVENT, listener);
  };
}

/** The app path of a location in hash mode: "#/onay/214" is "/onay/214", anything else "/". */
export function pathFromHash(hash: string): string {
  const path = hash.replace(/^#/, '').split('?')[0] ?? '';
  return path.startsWith('/') ? path : '/';
}

function currentPath(): string {
  return mode === 'hash' ? pathFromHash(window.location.hash) : window.location.pathname;
}

/** The href of an app path in the current mode. */
export function hrefFor(path: string): string {
  return mode === 'hash' ? `#${path}` : path;
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, currentPath, () => '/');
}

export function useRoute(): Route {
  return parseRoute(usePath());
}

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (path === currentPath()) return;
  // Hash mode keeps the page's own address (the preview's sub-path) and moves only the hash.
  const url = mode === 'hash' ? `${window.location.pathname}${window.location.search}#${path}` : path;
  if (options.replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
  window.scrollTo?.(0, 0);
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; children: ReactNode };

/** An anchor that navigates without a reload; modified clicks keep the browser default. */
export function Link({ to, onClick, children, ...rest }: LinkProps) {
  const handle = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={hrefFor(to)} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
